//frontend/src/pages/admin/Companies.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import CompanyModal from '../../components/admin/CompanyModal';
import { companyAPI } from '../../services/api';


const Companies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [resetCredentials, setResetCredentials] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (search) {
        params.search = search;
      }

      const response = await companyAPI.getAll(params);
      setCompanies(response.data.data || []);
    } catch (error) {
      console.error('Компани татахад алдаа:', error);
      alert('Компаниудыг татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedCompany(null);
    setShowModal(true);
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Энэ компанийг устгахдаа итгэлтэй байна уу?')) return;

    try {
      await companyAPI.delete(id);
      alert('✅ Амжилттай устгалаа!');
      fetchCompanies();
    } catch (error) {
      console.error('Устгахад алдаа:', error);
      const errorMessage = error.response?.data?.message || 'Устгахад алдаа гарлаа';
      alert('❌ ' + errorMessage);
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const confirmMessage = newStatus === 'active' 
      ? 'Компанийг идэвхжүүлэх үү?'
      : 'Компанийг идэвхгүй болгох уу?';
    
    if (!confirm(confirmMessage)) return;

    try {
      await companyAPI.updateStatus(id, newStatus);
      alert(`✅ Компани ${newStatus === 'active' ? 'идэвхжлээ' : 'идэвхгүй боллоо'}!`);
      fetchCompanies();
    } catch (error) {
      console.error('Статус өөрчлөхөд алдаа:', error);
      alert('❌ Статус өөрчлөхөд алдаа гарлаа');
    }
  };

  const handleResetPassword = async (companyId) => {
    if (!confirm('Компанийн админы нууц үгийг шинэчлэх үү?')) return;

    try {
      const response = await companyAPI.resetPassword(companyId);
      setResetCredentials(response.data.data.credentials);
    } catch (error) {
      console.error('Нууц үг шинэчлэхэд алдаа:', error);
      alert('❌ Нууц үг шинэчлэхэд алдаа гарлаа');
    }
  };

  const handleViewDetails = (companyId) => {
    navigate(`/admin/companies/${companyId}`);
  };

  const handleModalClose = (shouldRefresh) => {
    setShowModal(false);
    setSelectedCompany(null);
    if (shouldRefresh) {
      fetchCompanies();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCompanies();
  };

  const stats = {
    total: companies.length,
    active: companies.filter((c) => c.status === 'active').length,
    suspended: companies.filter((c) => c.status === 'suspended').length,
    inactive: companies.filter((c) => c.status === 'inactive').length,
  };

  if (loading && companies.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-xl text-gray-600">Уншиж байна...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Компаниуд</h1>
          <p className="text-sm md:text-base text-gray-600">
            Бүх хүргэлтийн компаниудын жагсаалт удирдах
          </p>
        </div>
        <button onClick={handleAdd} className="btn btn-primary shadow-lg">
          <span>➕</span>
          <span>Компани нэмэх</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🏢</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Нийт компани</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">✅</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Идэвхтэй</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">⏸️</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Түр хаагдсан</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.suspended}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">⭕</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Идэвхгүй</p>
              <p className="text-2xl font-bold text-gray-500">{stats.inactive}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Компанийн нэрээр хайх..."
              className="admin-input"
            />
          </div>
          <div className="w-full sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="admin-input"
            >
              <option value="all">Бүх статус</option>
              <option value="active">Идэвхтэй</option>
              <option value="suspended">Түр хаагдсан</option>
              <option value="inactive">Идэвхгүй</option>
            </select>
          </div>
          <button
            type="submit"
            className="btn btn-primary justify-center sm:w-auto"
          >
            <span>🔍</span>
            <span>Хайх</span>
          </button>
        </form>
      </div>

      {/* Companies Grid (Mobile & Tablet) */}
      <div className="block lg:hidden space-y-4 mb-6">
        {companies.length === 0 ? (
          <div className="admin-card text-center py-12">
            <span className="text-6xl mb-4 block">📭</span>
            <p className="text-gray-600 mb-4">Компани олдсонгүй</p>
            <button onClick={handleAdd} className="btn btn-primary">
              Эхний компаниа нэмнэ үү
            </button>
          </div>
        ) : (
          companies.map((company) => (
            <div key={company.id} className="admin-card hover:shadow-lg transition">
              {/* Logo & Name */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-20 h-20 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-200">
                  {company.logo_thumbnail || company.logo_url ? (
                    <img
                      src={`http://localhost:5000${company.logo_thumbnail || company.logo_url}`}
                      alt={company.company_name}
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl">
                      🏢
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg mb-1 truncate cursor-pointer hover:text-primary-600 transition" onClick={() => handleViewDetails(company.id)}>
                    {company.company_name}
                  </h3>
                  <span
                    className={`badge text-xs ${
                      company.status === 'active'
                        ? 'badge-success'
                        : company.status === 'suspended'
                        ? 'badge-warning'
                        : 'badge-gray'
                    }`}
                  >
                    {company.status === 'active'
                      ? 'Идэвхтэй'
                      : company.status === 'suspended'
                      ? 'Түр хаагдсан'
                      : 'Идэвхгүй'}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <span>📧</span>
                  <span className="truncate">{company.email || 'И-мэйл байхгүй'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span>📞</span>
                  <span>{company.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span>💰</span>
                  <span>
                    {company.commission_value.toLocaleString()}₮
                    <span className="text-xs ml-1">
                      ({company.commission_type === 'per_delivery' ? 'Хүргэлт тутамд' : 'Сарын'})
                    </span>
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleStatusToggle(company.id, company.status)}
                  className={`flex-1 px-3 py-2 rounded-lg transition text-sm font-medium flex items-center justify-center gap-1 ${
                    company.status === 'active'
                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                  }`}
                  title={company.status === 'active' ? 'Идэвхгүй' : 'Идэвхжүүлэх'}
                >
                  <span>{company.status === 'active' ? '⏸️' : '▶️'}</span>
                </button>
                <button
                  onClick={() => handleEdit(company)}
                  className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-sm font-medium flex items-center justify-center gap-1"
                  title="Засах"
                >
                  <span>✏️</span>
                </button>
                <button
                  onClick={() => handleResetPassword(company.id)}
                  className="px-3 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition text-sm font-medium flex items-center justify-center"
                  title="Нууц үг шинэчлэх"
                >
                  <span>🔑</span>
                </button>
                <button
                  onClick={() => handleDelete(company.id)}
                  className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition text-sm font-medium flex items-center justify-center"
                  title="Устгах"
                >
                  <span>🗑️</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Companies Table (Desktop) */}
      <div className="hidden lg:block admin-card">
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="w-20">ID</th>
                <th>Компани</th>
                <th>Холбоо барих</th>
                <th>Комисс</th>
                <th className="w-32">Статус</th>
                <th className="w-32">Үүссэн</th>
                <th className="w-52">Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12">
                    <span className="text-6xl mb-4 block">📭</span>
                    <p className="text-gray-600 mb-4">Компани олдсонгүй</p>
                    <button onClick={handleAdd} className="btn btn-primary">
                      Эхний компаниа нэмнэ үү
                    </button>
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.id}>
                    <td className="font-semibold text-gray-500">#{company.id}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-200">
                          {(company.logo_thumbnail || company.logo_url) ? (
                            <img
                              src={`http://localhost:5000${company.logo_thumbnail || company.logo_url}`}
                              alt={company.company_name}
                              className="max-w-full max-h-full object-contain p-1.5"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-xl">🏢</div>';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xl">
                              🏢
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 cursor-pointer hover:text-primary-600 transition" onClick={() => handleViewDetails(company.id)}>
                            {company.company_name}
                          </div>
                          {company.address && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              📍 {company.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="text-sm space-y-1">
                        <div className="flex items-center gap-2 text-gray-600">
                          <span>📧</span>
                          <span className="truncate">{company.email || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <span>📞</span>
                          <span>{company.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="text-sm">
                        <div className="font-semibold text-gray-900">
                          {company.commission_value.toLocaleString()}₮
                        </div>
                        <div className="text-gray-500 text-xs">
                          {company.commission_type === 'per_delivery'
                            ? 'Хүргэлт тутамд'
                            : 'Сарын'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          company.status === 'active'
                            ? 'badge-success'
                            : company.status === 'suspended'
                            ? 'badge-warning'
                            : 'badge-gray'
                        }`}
                      >
                        {company.status === 'active'
                          ? 'Идэвхтэй'
                          : company.status === 'suspended'
                          ? 'Түр хаагдсан'
                          : 'Идэвхгүй'}
                      </span>
                    </td>
                    <td className="text-gray-600 text-sm">
                      {new Date(company.created_at).toLocaleDateString('mn-MN')}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStatusToggle(company.id, company.status)}
                          className={`p-2 rounded-lg transition ${
                            company.status === 'active'
                              ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                              : 'bg-green-50 text-green-600 hover:bg-green-100'
                          }`}
                          title={company.status === 'active' ? 'Идэвхгүй болгох' : 'Идэвхжүүлэх'}
                        >
                          {company.status === 'active' ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(company)}
                          className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                          title="Засах"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleResetPassword(company.id)}
                          className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition"
                          title="Нууц үг шинэчлэх"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(company.id)}
                          className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                          title="Устгах"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset Credentials Modal */}
      {resetCredentials && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🔑</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Нууц үг шинэчлэгдлээ!
                </h2>
                <p className="text-gray-600">
                  Админы шинэ нэвтрэх мэдээлэл
                </p>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-6 border-2 border-blue-200">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span>🔐</span>
                  <span>Нэвтрэх мэдээлэл:</span>
                </h3>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Нэвтрэх нэр:</p>
                    <p className="font-mono font-bold text-lg text-gray-900">
                      {resetCredentials.username}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Шинэ нууц үг:</p>
                    <p className="font-mono font-bold text-lg text-gray-900">
                      {resetCredentials.password}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  ⚠️ {resetCredentials.message}
                </p>
              </div>

              <button
                onClick={() => setResetCredentials(null)}
                className="btn btn-primary w-full justify-center"
              >
                Ойлголоо
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Modal */}
      {showModal && (
        <CompanyModal
          company={selectedCompany}
          onClose={handleModalClose}
        />
      )}
    </AdminLayout>
  );
};

export default Companies;